import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_MOCK, userAdminApi } from '@/api/client';
import { ALL_OUS, ACTIVE_OUS, OU_TREE, MOCK_OU_CATALOG_CACHE_STATUS, type OUNode } from '@/data/mockOUs';
import type { OUItem, OUTreeNode, OuCatalogCacheStatusDto } from '@/api/types';
import { useUserContext } from '@/contexts/UserContext';

function adaptOU(item: OUItem): OUNode {
  return {
    key: item.id,
    label: item.name,
    active: item.isActive,
    gdpr: item.gdpr ?? false,
    type: item.type,
    azureAdGroupId: item.azureAdGroupId,
  };
}

function adaptTreeNode(node: OUTreeNode): OUNode {
  return {
    key: node.id,
    label: node.name,
    children: node.children.length > 0 ? node.children.map(adaptTreeNode) : undefined,
  };
}

/**
 * 🔴 Las unidades organizacionales del negocio son SEIS: FOSE, CENTRAL, MEXICO, LATAM, EUROPA y USA.
 * Son las que manda SuccessFactors y las únicas que deben salir en la aplicación.
 *
 * En la base hay 1 984, porque el catálogo que construía el Active Directory metía el país y el
 * departamento dentro del árbol: 1 978 descendientes con nombres repetidos —`department` sale 51
 * veces, `sin departamento` 22— que llenaban cada selector de OU de la app con ruido inservible.
 *
 * Se filtra por **raíz** (`parentOuId` vacío) y no por una lista de seis ids: los ids son distintos
 * en QA y en producción, así que una lista fija obligaría a una configuración por ambiente y a un
 * despliegue para tocarla. El criterio de raíz da hoy exactamente esas seis y se mantiene solo.
 *
 * Si el backend dejara de mandar `parentOuId`, el filtro deja pasar todo: se degrada al
 * comportamiento anterior en vez de vaciar los selectores.
 */
function esRaiz(item: OUItem) {
  return item.parentOuId == null || item.parentOuId.trim() === '';
}

/**
 * Defaults to `activeOnly: true` — most screens only care about OUs currently active in the rules
 * engine — and to `rootsOnly: true`: ver {@link esRaiz}.
 */
export function useOUs(options?: { activeOnly?: boolean; rootsOnly?: boolean }) {
  const { demoUser } = useUserContext();
  const tenantId = demoUser.tenant;
  const activeOnly = options?.activeOnly ?? true;
  const rootsOnly = options?.rootsOnly ?? true;

  return useQuery<OUNode[]>({
    queryKey: ['ous', tenantId, activeOnly, rootsOnly],
    queryFn: async () => {
      if (USE_MOCK) return activeOnly ? ACTIVE_OUS : ALL_OUS;
      const data = await userAdminApi.getOUs();
      const visibles = rootsOnly ? data.filter(esRaiz) : data;
      const adapted = visibles.map(adaptOU);
      return activeOnly ? adapted.filter(ouNode => ouNode.active) : adapted;
    },
  });
}

/**
 * Returns raw OUItem list for admin management (no adaptation).
 * Defaults to `activeOnly: true`; OUsSubTab passes `{ activeOnly: false }` since it's
 * the only screen that manages (and must be able to see/reactivate) inactive OUs.
 *
 * `enabled: false` evita pedir el catálogo completo cuando el llamador solo lo necesita como
 * respaldo (p. ej. PerfilView cae aquí solo si el backend no resolvió el nombre de la OU): con
 * `enabled` en `true` por defecto, cada visita a "Mi perfil" —una de las pantallas más visitadas—
 * pediría las OUs completas del tenant aunque el dato ya venga resuelto.
 */
export function useOUItems(options?: { activeOnly?: boolean; rootsOnly?: boolean; enabled?: boolean }) {
  const { demoUser } = useUserContext();
  const tenantId = demoUser.tenant;
  const activeOnly = options?.activeOnly ?? true;
  const rootsOnly = options?.rootsOnly ?? true;
  const enabled = options?.enabled ?? true;

  return useQuery<OUItem[]>({
    queryKey: ['ou-items', tenantId, activeOnly, rootsOnly],
    enabled,
    queryFn: async () => {
      const items = USE_MOCK
        ? ALL_OUS.map(o => ({
            id: o.key, name: o.label, isActive: o.active ?? true, gdpr: o.gdpr, type: o.type,
          }))
        : await userAdminApi.getOUs();
      const visibles = rootsOnly ? items.filter(esRaiz) : items;
      return activeOnly ? visibles.filter(ouItem => ouItem.isActive) : visibles;
    },
  });
}

export function useSetOuActive() {
  const qc = useQueryClient();
  return useMutation<void, Error, { ouId: string; isActive: boolean }>({
    mutationFn: async ({ ouId, isActive }) => {
      if (USE_MOCK) return;
      await userAdminApi.setOuActive(ouId, isActive);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ous'] });
      void qc.invalidateQueries({ queryKey: ['ou-items'] });
    },
  });
}

export function useUpdateOU() {
  const qc = useQueryClient();
  return useMutation<OUItem, Error, { ouId: string; name?: string; description?: string; adminUserId?: string; type?: string; gdpr?: boolean; timezone?: string; azureAdGroupId?: string }>({
    mutationFn: async ({ ouId, ...body }) => {
      if (USE_MOCK) return { id: ouId, name: body.name ?? '', isActive: true };
      return userAdminApi.updateOU(ouId, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ous'] });
      void qc.invalidateQueries({ queryKey: ['ou-items'] });
    },
  });
}

export function useDeleteOU() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (ouId) => {
      if (USE_MOCK) return;
      await userAdminApi.deleteOU(ouId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ous'] });
      void qc.invalidateQueries({ queryKey: ['ou-items'] });
    },
  });
}

/** Id real de la OU con flag GDPR activo (antes: literal 'europa' hardcodeado). Undefined si ninguna OU tiene gdpr:true. */
export function useGdprOuKey(): string | undefined {
  const { data: ous = [] } = useOUs({ activeOnly: false });
  return ous.find(o => o.gdpr)?.key;
}

/**
 * Estado de la caché del catálogo de OUs (catalog:ous:{tenantId}) — diagnóstico para
 * el panel de Monitoreo. Es la MISMA caché que alimenta el dropdown de OUs (useOUs).
 * Ver GET /v1/organizational-units/cache-status.
 */
export function useOuCatalogCacheStatus() {
  return useQuery<OuCatalogCacheStatusDto>({
    queryKey: ['ou-catalog-cache-status'],
    queryFn: async () => {
      if (USE_MOCK) return MOCK_OU_CATALOG_CACHE_STATUS;
      return userAdminApi.getOuCatalogCacheStatus();
    },
    staleTime: 30_000,
    retry: false,
  });
}

async function fetchOUTree(ouId: string): Promise<OUNode | null> {
  if (!ouId) return null;
  if (USE_MOCK) return OU_TREE.find(o => o.key === ouId) ?? null;
  const data = await userAdminApi.getOUTree(ouId);
  return adaptTreeNode(data);
}

export function useOUTree(ouId: string | null) {
  const { demoUser } = useUserContext();
  const tenantId = demoUser.tenant;

  return useQuery<OUNode | null>({
    queryKey: ['ou-tree', ouId, tenantId],
    enabled: !!ouId,
    queryFn: () => fetchOUTree(ouId ?? ''),
  });
}
